import {
    getGetParcelUrl,
    getUpdateParcelUrl
} from 'generatedTypes/client/services/parcel-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { CLIENTS_API } from './constants';
import type {
    CreateFarm,
    FetchFarm,
    ParcelResponse,
    ParcelWithClientResponse,
    UpdateFarm
} from './types';

const useFarm = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost, authenticatedPut } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchFarm: FetchFarm = async ({ id }) => {
        try {
            setIsLoading(true);
            const url = getGetParcelUrl(id);

            return await withRetry(
                () => authenticatedGet<ParcelWithClientResponse>(url),
                CONNECTION_ERROR,
                () => fetchFarm({ id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const createFarm: CreateFarm = async ({ clientId, farm }) => {
        try {
            setIsLoading(true);
            const url = `${CLIENTS_API}/${clientId}/parcels`;

            return await withRetry(
                () => authenticatedPost<ParcelResponse>(url, JSON.stringify(farm)),
                SAVE_FAILED,
                () => createFarm({ clientId, farm })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateFarm: UpdateFarm = async ({ farm, id }) => {
        try {
            setIsLoading(true);
            const url = getUpdateParcelUrl(id);

            return await withRetry(
                () => authenticatedPut<ParcelResponse>(url, JSON.stringify(farm)),
                SAVE_FAILED,
                () => updateFarm({ farm, id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return {
        createFarm,
        fetchFarm,
        isLoading,
        updateFarm
    };
};

export default useFarm;
